import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  FileText,
  Info,
  X,
  CheckCircle,
  Lock,
} from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Stepper } from "../../../foundation/altzero_base/base/components/stepper";
import {
  DocumentFormProvider,
  useDocumentForm,
} from "../contexts/DocumentFormContext";
import { BatchParsingProvider } from "../contexts/BatchParsingContext";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import {
  canAccessStep,
  firstAllowedStep,
  nextAllowedStep,
  previousAllowedStep,
} from "../lib/tenderStepPermissions";
import ScopingAILayout from "../components/ScopingAILayout";

// Import our step components
import ClientInfoStep from "../components/ClientInfoStep";
// import TenderDetailsStep from "../components/TenderDetailsStep";  // REMOVED: Step 2 removed
import OffersStep from "../components/OffersStep";
import CompareAndSelectStep from "../components/CompareAndSelectStep";
import RecommendedOfferStep from "../components/RecommendedOfferStep";
import TemplateSelectionStep from "../components/TemplateSelectionStep";
import OfferDetailsStep from "../components/OfferDetailsStep";
import DocumentOverviewStep from "../components/DocumentOverviewStep";
import RetailerCommunicationStep from "../components/RetailerCommunicationStep";
import EmailProcessingStep from "../components/EmailProcessingStep";

// Main document content component that manages the steps
function DocumentCreator() {
  const {
    currentStep,
    isLoading,
    referenceDocument,
    handleBack,
    handleNext,
    handleRemoveReference,
    clearSessionData,
    loadTenderWorkflow,
    isLoadingTender,
    tenderId,
    goToStep,
    canNavigateToStep,
    setCurrentStep,
  } = useDocumentForm();

  // Step-level permissions. UI gating only — see lib/tenderStepPermissions.ts.
  const { can, isLoading: isLoadingPermissions } = usePermissions();

  const stepIsAllowed = canAccessStep(can, currentStep);
  const hasAnyStep = firstAllowedStep(can) !== null;

  /**
   * Which way the user was travelling, so a redirect off a denied step keeps that direction.
   *
   * Pressing Next on step 5 with only {5, 9} granted must land on 9, not bounce back to 5; the
   * same bounce on Back must land on 5, not jump to 9. Only the last step the user was ALLOWED
   * to stand on is recorded, because a denied step is never a real position.
   */
  const lastAllowedStepRef = useRef(currentStep);

  useEffect(() => {
    if (isLoadingPermissions) return;
    if (stepIsAllowed) {
      lastAllowedStepRef.current = currentStep;
      return;
    }

    const movingForward = currentStep >= lastAllowedStepRef.current;
    const target = movingForward
      ? nextAllowedStep(can, currentStep) ?? previousAllowedStep(can, currentStep)
      : previousAllowedStep(can, currentStep) ?? nextAllowedStep(can, currentStep);

    // No reachable step at all: fall through to the no-access panel instead of looping.
    if (target === null || target === currentStep) return;

    // setCurrentStep, not goToStep: this is not the user leaving a step, so nothing of theirs is
    // pending to flush or save, and the tender's stored current_step must not be rewritten
    // because of who happens to be looking at it.
    lastAllowedStepRef.current = target;
    setCurrentStep(target);
  }, [can, currentStep, isLoadingPermissions, stepIsAllowed, setCurrentStep]);

  const canNavigateToStepWithPermissions = useMemo(
    () => (step: number) => canNavigateToStep(step) && canAccessStep(can, step),
    [canNavigateToStep, can]
  );



  const [searchParams] = useSearchParams();
  const [preSelectedTemplate, setPreSelectedTemplate] = useState(null);
  const [hasLoadedTender, setHasLoadedTender] = useState(false);

  // Check for tender parameter and load existing tender
  useEffect(() => {
    const tenderParam = searchParams.get('tender');

    if (tenderParam && !isLoadingTender && !hasLoadedTender) {
      setHasLoadedTender(true);

      const editMode = searchParams.get('editMode') === 'true';
      const stepParam = parseInt(searchParams.get('step') || '9', 10);
      const editStep = isNaN(stepParam) ? 9 : stepParam;

      loadTenderWorkflow(tenderParam, editMode ? { editMode: true, editStep } : undefined).then((result) => {
        if (!result?.success) {
          setHasLoadedTender(false);
        }
      });
    }
  }, [searchParams, loadTenderWorkflow, isLoadingTender, hasLoadedTender]);

  // Original session clearing logic (now only for new tenders)
  const initializeNewSession = () => {
    clearSessionData();
    const now = Date.now();
    const newSessionId = `session_${now}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem("scopingai_session_id", newSessionId);
    sessionStorage.setItem("scopingai_session_timestamp", now.toString());
  };

  // Check for template parameter on component mount (only for new tenders)
  useEffect(() => {
    const templateParam = searchParams.get("template");
    const tenderParam = searchParams.get('tender');

    // Only handle template parameter if not loading an existing tender
    if (templateParam && !tenderParam) {
      try {
        const templateInfo = JSON.parse(decodeURIComponent(templateParam));
        setPreSelectedTemplate(templateInfo);
        initializeNewSession();
      } catch (error) {
        // Silent error handling
      }
    } else if (!tenderParam && !isLoadingTender) {
      // No tender or template - initialize new session (but not if we're loading a tender)
      initializeNewSession();
    }
  }, [searchParams, isLoadingTender]);

  // If loading tender, show loading state
  if (isLoadingTender) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tender...</p>
        </div>
      </div>
    );
  }

  // Function to render the current step
  const renderStep = () => {
    // Permissions decide which steps exist for this role; render nothing until they are known,
    // otherwise an empty permission set briefly locks the wizard for everyone on first paint.
    if (isLoadingPermissions) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Checking your access...</p>
        </div>
      );
    }

    // Opening this page with no ?tender= is starting a brand new tender, which tender.create
    // governs. The buttons that lead here are already hidden without it (Dashboard,
    // ScopingProposals), so this only closes the direct-URL route — a role restricted to a couple
    // of steps of someone else's tender has no business creating one of its own.
    if (!searchParams.get("tender") && !can("tender.create")) {
      return (
        <div className="mx-auto max-w-lg rounded-lg border border-muted bg-muted/30 p-8 text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h3 className="font-semibold mb-1">You cannot create new tenders</h3>
          <p className="text-sm text-muted-foreground">
            Your role can work on existing tenders only. Open one from the proposals list, or ask
            an administrator for the Create Tenders permission.
          </p>
        </div>
      );
    }

    if (!hasAnyStep) {
      return (
        <div className="mx-auto max-w-lg rounded-lg border border-muted bg-muted/30 p-8 text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h3 className="font-semibold mb-1">No access to the tender workflow</h3>
          <p className="text-sm text-muted-foreground">
            Your role has not been given access to any step of this workflow. Ask an administrator
            to grant the steps you need in Settings &gt; Permissions.
          </p>
        </div>
      );
    }

    // Denied step with somewhere else to go: the effect above is already moving the user there.
    if (!stepIsAllowed) return null;

    console.log(
      "🔄 DocumentsNew - Current step:",
      currentStep,
      "Pre-selected template:",
      preSelectedTemplate
    );

    // Handle existing proposal flow (skip template selection)
    if (preSelectedTemplate?.type === "existing_proposal") {
      switch (currentStep) {
        case 1:
          return <ClientInfoStep onNext={handleNext} onBack={handleBack} />;
        case 2:
          return <DocumentOverviewStep onNext={handleNext} onBack={handleBack} />;
        default:
          return null;
      }
    }

    // Normal flow: Tender (client+sites) -> Retailer Communication -> Email Processing -> Offers -> Compare & Select -> Recommended -> Template -> Offer Details -> Overview
    switch (currentStep) {
      case 1:
        return <ClientInfoStep onNext={handleNext} onBack={handleBack} />;
      case 2:
        return <RetailerCommunicationStep onNext={handleNext} onPrevious={handleBack} />;
      case 3:
        return <EmailProcessingStep onNext={handleNext} onPrevious={handleBack} />;
      case 4:
        return <OffersStep onNext={handleNext} onPrevious={handleBack} />;
      case 5:
        return <CompareAndSelectStep onNext={handleNext} onPrevious={handleBack} />;
      case 6:
        return <RecommendedOfferStep onNext={handleNext} onPrevious={handleBack} />;
      case 7:
        return (
          <TemplateSelectionStep
            onNext={handleNext}
            onBack={handleBack}
            preSelectedTemplate={preSelectedTemplate}
          />
        );
      case 8:
        return <OfferDetailsStep onNext={handleNext} onBack={handleBack} />;
      case 9:
        return <DocumentOverviewStep onNext={handleNext} onBack={handleBack} />;
      default:
        return null;
    }
  };

  return (
    <ScopingAILayout>
      <div className="w-full h-full flex flex-col overflow-hidden">
        <div className="mb-4 flex-shrink-0">
          <h1 className="text-2xl font-bold">Create New Tendering Proposal</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Follow the steps to create your energy tendering proposal
          </p>
        </div>

        {/* Template Pre-selection Banner */}
        {preSelectedTemplate && (
          <div className="mb-4 flex-shrink-0 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle
                className="text-green-600 dark:text-green-400 mt-0.5"
                size={20}
              />
              <div className="flex-1">
                <h3 className="font-semibold text-green-800 dark:text-green-200 mb-1">
                  {preSelectedTemplate.type === "existing_proposal"
                    ? "Using Existing Proposal as Template"
                    : "Template Pre-selected"}
                </h3>
                <p className="text-green-700 dark:text-green-300 text-sm mb-2">
                  {preSelectedTemplate.type === "existing_proposal"
                    ? `Creating new proposal based on "${preSelectedTemplate.title}". The template selection step will be skipped.`
                    : `Using "${preSelectedTemplate.title}" as your document template. This template will be automatically selected in step 2.`}
                </p>
                <div className="flex items-center gap-4 text-xs text-green-600 dark:text-green-400">
                  <span className="flex items-center gap-1">
                    <FileText size={12} />
                    {preSelectedTemplate.type === "existing_proposal"
                      ? `Originally for ${preSelectedTemplate.original_client}`
                      : (preSelectedTemplate.source || "Template")}
                  </span>
                  <span>•</span>
                  <span>
                    {preSelectedTemplate.type === "existing_proposal"
                      ? "Template selection step skipped"
                      : "Template will be locked for this session"}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPreSelectedTemplate(null);
                  // Remove template parameter from URL
                  const newUrl = new URL(window.location.href);
                  newUrl.searchParams.delete("template");
                  window.history.replaceState({}, "", newUrl);
                }}
                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        )}

        {referenceDocument && (
          <div className="mb-4 flex-shrink-0">
            <div className="bg-primary/10 rounded-lg p-4 flex items-start justify-between">
              <div>
                <div className="flex items-center">
                  <FileText size={18} className="text-primary mr-2" />
                  <h3 className="font-medium">Using Reference Document</h3>
                </div>
                <p className="text-sm mt-1">
                  <span className="font-medium">{referenceDocument.title}</span>{" "}
                  - The AI will use this document's structure and format as a
                  reference.
                </p>
                <p className="text-xs text-primary mt-2 flex items-center">
                  <Info size={12} className="mr-1" />
                  Using "Custom" template based on this reference document
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveReference}
                className="text-muted-foreground"
              >
                <X size={16} />
                <span className="sr-only">Remove reference</span>
              </Button>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="mb-4 flex-shrink-0">
            <div className="bg-muted rounded-lg p-6 flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-center font-medium">
                Loading reference document...
              </p>
              <p className="text-center text-sm text-muted-foreground mt-1">
                This may take a moment while we fetch the document content.
              </p>
            </div>
          </div>
        )}

        <div className="flex-shrink-0">
          <Stepper
            currentStep={
              preSelectedTemplate?.type === "existing_proposal"
                ? currentStep === 1 ? 1 : currentStep === 2 ? 2 : currentStep === 3 ? 3 : currentStep
                : currentStep
            }
            steps={
              preSelectedTemplate?.type === "existing_proposal"
                ? [
                  { label: "Client", description: "Info" },
                  { label: "Overview", description: "Generate" },
                ]
                : [
                  { label: "Tender", description: "Info" },
                  { label: "Communication", description: "RFI" },
                  { label: "Processing", description: "Email" },
                  { label: "Offers", description: "Pricing" },
                  { label: "Compare", description: "Select" },
                  { label: "Recommend", description: "Finalize" },
                  { label: "Template", description: "Select" },
                  { label: "Details", description: "Costs" },
                  { label: "Overview", description: "Generate" },
                ]
            }
            onStepClick={
              preSelectedTemplate?.type === "existing_proposal"
                ? undefined
                : (step) => { void goToStep(step); }
            }
            canNavigate={
              preSelectedTemplate?.type === "existing_proposal"
                ? undefined
                : canNavigateToStepWithPermissions
            }
          />
        </div>

        {/* Step content - scrollable area that fills remaining viewport height */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-6 pb-6">
          {renderStep()}
        </div>

        {/* Progress UI is now handled by DocumentOverviewStep.tsx Dialog modal */}
      </div>
    </ScopingAILayout>
  );
}

// Default export wrapped in Suspense and provider
export default function NewDocument() {
  const [searchParams] = useSearchParams();
  const [preSelectedTemplate, setPreSelectedTemplate] = useState(null);

  // Handle pre-selected template from URL
  useEffect(() => {
    const templateParam = searchParams.get("template");
    if (templateParam) {
      try {
        const template = JSON.parse(decodeURIComponent(templateParam));
        setPreSelectedTemplate(template);
      } catch (error) {
        console.error("Failed to parse template parameter:", error);
      }
    }
  }, [searchParams]);

  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">
            Loading document creator...
          </p>
        </div>
      }
    >
      <DocumentFormProvider preSelectedTemplate={preSelectedTemplate}>
        <BatchParsingProvider>
          <DocumentCreator />
        </BatchParsingProvider>
      </DocumentFormProvider>
    </Suspense>
  );
}
